"use client";

import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { blocksToString, limitTextBlock } from "@/lib/blocks";
import useLocaleLegacy from "@/hooks/use-locale-legacy";
import locale from "@/locales/news-event.json";
import useGetCMS from "@/hooks/use-get-cms";
import Pagination from "@/components/Pagination";
import { useParams } from "next/navigation";
import { useLocale } from "next-intl";
import { Link, useRouter } from "@/src/i18n/navigation";

import { useQueryState } from "nuqs";
import EmbededInstagram from "@/components/embed-instagram";
import { PaginationResponse } from "@/app/models/response";

interface NewsEventProps {
  social_media_title: string;
  social_media_link: string;
  thumbnailArticles: PaginationResponse<
    {
      slug: string;
      title: string;
      image: string;
      description: any[];
      custom_date: string;
    }[]
  >;
  news: PaginationResponse<
    {
      id: string;
      slug: string;
      title: string;
      image: string;
      description: any[];
      custom_date: string;
    }[]
  >;
}

export const BASE_SORT = "custom_date:desc";
export const BASE_PAGE = 1;

export const BASE_FILTER_NEWS = {
  pagination: {
    page: 1,
    pageSize: 5,
  },
  filters: {
    is_thumbnail: { $null: true },
  },
  sort: BASE_SORT,
  pLevel: 2,
};

export default function Client(props: NewsEventProps) {
  const lang = useLocale();
  const router = useRouter();
  const { page = 1 } = useParams();
  const [sortBy, setSortBy] = useQueryState("sort", {
    defaultValue: BASE_SORT,
  });
  const [category, setCategory] = useQueryState("category", {
    defaultValue: "",
  });

  const { data: thumbnailArticles } = useGetCMS<PaginationResponse<{}>>({
    key: "news-and-events-thumbnails" + category,
    collection: "new-and-events",
    config: {
      pLevel: 2,
      locale: lang,
      pagination: {
        limit: 1,
      },
      filters: {
        ...(category && { category: { $eq: category } }),
        is_thumbnail: { $eq: true },
      },
      sort: "custom_date:desc",
    },
    fallbackData: props.thumbnailArticles,
    swrConfig: {
      revalidateOnMount: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  });
  const thumbnailNews = thumbnailArticles?.data?.[0] || null;

  const { t } = useLocaleLegacy(locale);

  const [openSortBy, setOpenSortBy] = useState(false);
  const [openCategory, setOpenCategory] = useState(false);

  const [filter, setFilter] = useState({
    ...BASE_FILTER_NEWS,
    locale: lang,
    pagination: {
      ...BASE_FILTER_NEWS.pagination,
      page: page,
    },
    filters: {
      ...BASE_FILTER_NEWS.filters,
      ...(thumbnailNews?.id && {
        id: { $ne: thumbnailNews.id },
      }),
    },
  });

  const { data: newsResponse } = useGetCMS({
    key: `new-and-events-${JSON.stringify(filter)}`,
    collection: "new-and-events",
    config: filter,
    fallbackData: props.news,
    swrConfig: {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  });

  const news = newsResponse?.data || [];
  const meta = newsResponse?.meta || {
    pagination: {
      pageCount: 1,
    },
  };

  useEffect(() => {
    const titleEl = document.getElementById("news-title");
    if (titleEl) {
      titleEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [newsResponse]);

  const onChangePage = (page: number) => {
    router.push(`/news-events/page/${page}`, { locale: lang, scroll: false });
  };

  const onChangeCategory = (category: "show_all" | "event" | "news") => {
    let _category: string | undefined = undefined;
    if (category === "show_all") {
      _category = undefined;
    }
    if (category === "event") {
      _category = "event";
    }
    if (category === "news") {
      _category = "news";
    }

    setFilter((prev) => ({
      ...BASE_FILTER_NEWS,
      locale: lang,
      filters: {
        ...prev.filters,
        category: {
          $eq: _category,
        },
      },
    }));

    setCategory(_category || "");
  };

  const onChangeSortBy = (sortBy: "most_recent" | "most_relevant") => {
    let _sort = "";
    if (sortBy === "most_recent") {
      _sort = BASE_SORT;
    }
    // if (sortBy === "most_relevant") {
    //   _sort = "";
    // }

    setSortBy(_sort);
    setFilter((prev) => ({
      ...prev,
      sort: _sort,
      pagination: {
        ...prev.pagination,
        page: 1, // Reset to page 1 when changing sort
      },
    }));
  };

  const sortValue = useMemo(() => {
    if (sortBy === BASE_SORT) {
      return "most_recent";
    }
    return "most_relevant";
  }, [sortBy]);

  const categoryValue = useMemo(() => {
    if (category === "event") {
      return "event";
    }
    if (category === "news") {
      return "news";
    }
    return "show_all";
  }, [category]);

  return (
    <>
      <div className="bg-coffebrown py-8 xl:py-28 *:whitespace-normal pt-20 xl:pt-0 xl:mt-20">
        <div className="w-full xl:max-w-screen-xl mx-auto px-9 xl:px-0 pt-12 xl:pt-20">
          <p
            id="news-title"
            className="text-white text-2xl xl:text-6xl text-center font-pluto"
          >
            {t("title")}
          </p>

          <div className="flex text-white pt-7 xl:mt-10">
            <div className="text-[10px] xl:text-3xl">
              <span className="font-bold">{t("sort_by")}</span>:
              <DropdownMenu open={openSortBy} onOpenChange={setOpenSortBy}>
                <DropdownMenuTrigger
                  className="cursor-pointer ml-2"
                  onMouseEnter={() => setOpenSortBy(true)}
                  onMouseLeave={() => setOpenSortBy(false)}
                >
                  {t(sortValue)}
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="xl:w-[var(--radix-dropdown-menu-trigger-width)] xl:min-w-[165px]"
                  onMouseEnter={() => setOpenSortBy(true)}
                  onMouseLeave={() => setOpenSortBy(false)}
                  sideOffset={-4}
                >
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => onChangeSortBy("most_recent")}
                  >
                    {t("most_recent")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="ml-auto text-[10px] xl:text-3xl">
              <span className="font-bold">{t("category")}</span>:
              <DropdownMenu open={openCategory} onOpenChange={setOpenCategory}>
                <DropdownMenuTrigger
                  onMouseEnter={() => setOpenCategory(true)}
                  onMouseLeave={() => setOpenCategory(false)}
                  className="cursor-pointer ml-2"
                >
                  {categoryValue === "show_all" && t("show_all")}
                  {categoryValue === "event" && "Event"}
                  {categoryValue === "news" && "News"}
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="xl:w-[var(--radix-dropdown-menu-trigger-width)] xl:min-w-[201px]"
                  onMouseEnter={() => setOpenCategory(true)}
                  onMouseLeave={() => setOpenCategory(false)}
                  sideOffset={-4}
                >
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => onChangeCategory("show_all")}
                  >
                    {t("show_all")}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => onChangeCategory("event")}
                  >
                    Event
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => onChangeCategory("news")}
                  >
                    News
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {thumbnailNews && (
            <div className="mt-3.5">
              <Link
                locale={lang}
                prefetch
                href={`/news-events/${thumbnailNews.slug}`}
              >
                <img
                  src={`${process.env.NEXT_PUBLIC_BASE_STATIC_URL}${thumbnailNews.images?.[0]?.url}`}
                  alt=""
                  className="rounded-[9px] xl:rounded-[2.5rem] object-cover w-full h-[242px] xl:h-[30rem]"
                />

                <div className="flex pt-5 xl:pt-7 flex-col xl:flex-row">
                  <div className="flex-1">
                    <p
                      className="font-medium xl:font-normal text-white xl:text-gold text-xl xl:text-3xl font-pluto line-clamp-3"
                      dangerouslySetInnerHTML={{ __html: thumbnailNews.title }}
                    ></p>
                  </div>
                  <div className="flex-1">
                    <div className="text-white xl:pl-14 xl:text-2xl line-clamp-3">
                      {blocksToString(thumbnailNews.description.slice(0, 4))}
                    </div>
                  </div>

                  <p className="text-white text-[10px] xl:text-xl mt-4 block xl:hidden">
                    {dayjs(news.custom_date).format("MMMM DD, YYYY")}
                  </p>
                </div>
              </Link>
            </div>
          )}

          <hr
            id="divider-news"
            className="w-full border border-b-[.5px] border-b-white my-20 hidden xl:block"
          />

          <div className="flex flex-col space-y-8 mt-9 xl:mt-0">
            {news.map((data) => (
              <Link
                locale={lang}
                prefetch
                key={data.id}
                href={`/news-events/${data.slug}`}
              >
                <div className="flex flex-col xl:flex-row">
                  <div className="flex-1">
                    <img
                      src={`${process.env.NEXT_PUBLIC_BASE_STATIC_URL}${data.images?.[0]?.url}`}
                      loading="lazy"
                      alt=""
                      className="object-cover w-full rounded-[9px] xl:rounded-[30px] h-60 xl:h-80"
                    />
                  </div>
                  <div className="flex-1 flex flex-col gap-y-2 xl:gap-y-4 pt-5 xl:pt-0 xl:pl-12">
                    <p className=" text-white xl:text-xl hidden xl:block">
                      {dayjs(data.custom_date).format("MMMM DD, YYYY")}
                    </p>
                    <h4
                      className=" font-medium xl:font-normal text-white xl:text-gold text-xl xl:text-2xl font-pluto line-clamp-3"
                      dangerouslySetInnerHTML={{ __html: data.title }}
                    ></h4>
                    <div className="text-white text-[12px] xl:text-xl whitespace-normal line-clamp-3">
                      {blocksToString(data.description.slice(0, 3))}
                    </div>
                    <p className="text-white text-[10px] block xl:hidden">
                      {dayjs(data.custom_date).format("MMMM DD, YYYY")} | News
                    </p>
                    <p className="text-white capitalize hidden xl:block">
                      {data.category}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <Pagination
            currentPage={page}
            pageCount={meta.pagination.pageCount}
            pageLimit={5}
            onPageChange={onChangePage}
          />

          <hr className="bg-white mt-12 xl:mt-24 mb-8 xl:mb-16" />
          <h4
            dangerouslySetInnerHTML={{ __html: props.social_media_title }}
            className="text-white text-center text-[13px] xl:text-4xl mt-10 xl:mt-0 mb-8 font-pluto"
          ></h4>

          <EmbededInstagram social_media_link={props.social_media_link} />
        </div>
      </div>
    </>
  );
}
